import type { Update } from '@ngrx/entity';
import { groupBy, mapValues, xor } from 'lodash-es';
import type { Employee, EmployeeGroup } from '@fc-core';

export function buildEmployeeUpdatesForLoadedGroups(
  employees: Employee[],
  loadedGroups: EmployeeGroup[],
): Update<Employee>[] {
  const memberships = loadedGroups.flatMap((group) =>
    (group.employees ?? []).map((employeeId) => ({
      employeeId,
      groupId: group.id,
    })),
  );
  const groupIdsByEmployeeId = mapValues(
    groupBy(memberships, 'employeeId'),
    (employeeMemberships) =>
      employeeMemberships.map((membership) => membership.groupId),
  );
  return employees.flatMap((employee) => {
    if (!employee.id) return [];
    const loadedGroupIds = groupIdsByEmployeeId[employee.id] ?? [];
    if (!xor(loadedGroupIds, employee.groups ?? []).length) return [];
    return [{ id: employee.id, changes: { groups: loadedGroupIds } }];
  });
}

export function buildEmployeeUpdatesForGroupMembers(
  employees: Employee[],
  groupId: number,
  memberIds: string[] | undefined,
): Update<Employee>[] {
  if (!memberIds) return [];
  const memberIdSet = new Set(memberIds);
  return employees.flatMap((employee) => {
    if (!employee.id) return [];
    const groupIds = employee.groups ?? [];
    const isMember = memberIdSet.has(employee.id);
    if (groupIds.includes(groupId) === isMember) return [];
    return [
      {
        id: employee.id,
        changes: {
          groups: isMember
            ? [...groupIds, groupId]
            : groupIds.filter((id) => id !== groupId),
        },
      },
    ];
  });
}

export function buildGroupUpdatesForEmployeeGroups(
  groups: EmployeeGroup[],
  employeeId: string | undefined,
  employeeGroupIds: number[] | undefined,
): Update<EmployeeGroup>[] {
  if (!employeeId || !employeeGroupIds) return [];
  const employeeGroupIdSet = new Set(employeeGroupIds);
  return groups.flatMap((group) => {
    const memberIds = group.employees ?? [];
    const isMember = employeeGroupIdSet.has(group.id);
    if (memberIds.includes(employeeId) === isMember) return [];
    return [
      {
        id: group.id,
        changes: {
          employees: isMember
            ? [...memberIds, employeeId]
            : memberIds.filter((id) => id !== employeeId),
        },
      },
    ];
  });
}
